import React, { useContext, useEffect, useState } from 'react';
import { Card, message } from '@brokerbay/design-components';
import styled from 'styled-components';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import { getAnsweringServicePayerAndType } from 'api/AnsweringServiceController';
import { findActiveSubscription as findAnsweringServiceSubscription } from 'api/AnsweringSubscriptionController';
import type { AnsweringServicePayer } from '@/app-package';
import { AuthContext } from 'contexts';
import CurrentServicesSection from './components/CurrentServicesSection';
import AvailableServicesSection from './components/AvailableServicesSection';
import { PlanTier, OrgPlanPayer } from './components/OrgPlanCard';

export interface AllSubscriptionsCardProps {
  /** ID of the user viewing this page */
  userId: string;
  /** Whether these are the subscriptions for an indivdual user or for a brokerage paying for all its users */
  viewingLevel: 'user' | 'org';
  /** Callback function when the Org Plan (i.e. Tier) button is clicked */
  onOrgPlanClick: () => void;
  /** Callback function when the Answering Service button is clicked */
  onAnsweringServiceClick: () => void;
}

/**
 * Main subscriptions card displaying a user or brokerage's current services
 * (i.e. active subscriptions) and available services they can sign up for.
 */
const AllSubscriptionsCard: React.FC<AllSubscriptionsCardProps> = ({
  userId,
  viewingLevel,
  onOrgPlanClick,
  onAnsweringServiceClick,
}) => {
  // #region States & variables

  const {
    answeringPortalConfig,
    auth: { org: userBrokerage },
    isOrgOnBrokeragePlan,
    isOrgPlanUpgraded,
  } = useContext(AuthContext);
  /** The type of Org Plan this brokerage is on. Agent Plan (Tier 1) or Brokerage Plan (Tier 2). */
  const currentOrgPlan: PlanTier =
    isOrgOnBrokeragePlan || isOrgPlanUpgraded ? 'tier2' : 'tier1';
  /** How the Org Plan subscription is billed */
  const orgPlanPayer: OrgPlanPayer =
    isOrgOnBrokeragePlan && !isOrgPlanUpgraded ? 'boardOrMls' : 'brokerage';
  /** What type of Answering Service this user/brokerge has (or is eligible to sign up for) */
  const [answeringServiceType, setAnsweringServiceType] =
    useState<AnsweringServiceType>(AnsweringServiceType.LiveConciergePlus);
  /** Who should pay for any Answering Service subscription in this brokerage */
  const [answeringServicePayer, setAnsweringServicePayer] =
    useState<AnsweringServicePayer>('user');
  /** Whether the user has an active Answering Service subscription or is currently unsubscribed */
  const [hasActiveAnsweringService, setHasActiveAnsweringService] =
    useState<boolean>(false);

  // #endregion States & variables
  // #region Methods

  // Run on first page load
  useEffect(() => {
    /** Method to grab the Answering Service subscription from the db and set front end variables appropriately */
    const populateAnsweringSubDetails = async () => {
      try {
        const { payer, type } = await getAnsweringServicePayerAndType({
          orgId: userBrokerage.id,
        });
        setAnsweringServiceType(type);
        setAnsweringServicePayer(payer);
        if (
          type === AnsweringServiceType.Legacy ||
          (type === AnsweringServiceType.LiveConciergePlus &&
            payer === 'boardOrMls')
        ) {
          // We end here if there is no option available for the user/brokerage to upgrade to.
          // (i.e. it's legacy or the board/MLS already pays for LCS)
          setHasActiveAnsweringService(true);
        } else {
          // The burden is on a brokerage or user to pay an LCS subscription,
          // so check to see if they've signed up.
          const foundSubscription = await findAnsweringServiceSubscription({
            subscriptionLevel: viewingLevel,
            userId,
          });

          if (foundSubscription) {
            setHasActiveAnsweringService(true);
            setAnsweringServiceType(foundSubscription.serviceType);
            if (
              foundSubscription.serviceType ===
              AnsweringServiceType.LiveConciergePlus
            ) {
              // If the found subscription was an LCS subscription,
              // at this point we know it is either a brokerage or user paid one
              // (board/MLS was filtered out above) so check the billingProductName.
              foundSubscription.billingProductName === 'SHO-LCS-BROK'
                ? setAnsweringServicePayer('brokerage')
                : setAnsweringServicePayer('user');
            }
          } else {
            setHasActiveAnsweringService(false);
          }
        }
      } catch {
        message.error(
          'Error attempting to fetch Answering Service subscription details',
        );
      }
    };

    // Fetch & fill in all the Answering Service subscription details
    populateAnsweringSubDetails();
  }, [answeringPortalConfig, userBrokerage, userId, viewingLevel]);

  // #endregion Methods
  // #region JSX to return

  return (
    <StyledCard>
      <ContentContainer>
        <CurrentServicesSection
          currentOrgPlan={currentOrgPlan}
          orgPlanPayer={orgPlanPayer}
          hasActiveAnsweringService={hasActiveAnsweringService}
          answeringServiceType={answeringServiceType}
          answeringServicePayer={answeringServicePayer}
          onOrgPlanClick={onOrgPlanClick}
          onAnsweringServiceClick={onAnsweringServiceClick}
        />
        <AvailableServicesSection
          hasActiveAnsweringService={hasActiveAnsweringService}
          answeringServiceType={answeringServiceType}
          answeringServicePayer={answeringServicePayer}
          onAnsweringServiceClick={onAnsweringServiceClick}
        />
      </ContentContainer>
    </StyledCard>
  );
};

// #endregion JSX to return

export default AllSubscriptionsCard;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 24px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
